import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Header } from "../components/Header";
import { TeamBadge } from "../components/TeamBadge";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { kickoffTimeTz, kickoffDate } from "../lib/datetime";
import { ShareCard } from "../components/ShareCard";
import { trackEvent } from "../lib/analytics";
import {
  CheckCircle, XCircle, Trophy, Target, Flag, Medal, Crown, Sparkle,
  Fire, Users, Plus, Copy, Sword, LockKey, CaretLeft, ShareNetwork, Lightning, Prohibit,
} from "@phosphor-icons/react";

// Audit conformité F6 : aucune mention de cadeau tant que le règlement du Dojo n'est pas validé
// juridiquement et publié. Activer avec REACT_APP_DOJO_PRIZES_ENABLED=true au build.
const DOJO_PRIZES_ENABLED = process.env.REACT_APP_DOJO_PRIZES_ENABLED === "true";

const hasSub = (u) => u?.role === "admin" || ["starter", "pro", "vip"].includes(u?.plan);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Avatar rond du classement : photo de profil si dispo, sinon initiale.
function DojoAvatar({ row, size = 34, ring = null }) {
  const style = { width: size, height: size, ...(ring ? { boxShadow: `0 0 0 2.5px ${ring}` } : {}) };
  if (row?.avatar_url) {
    return (
      <img src={`${BACKEND_URL}${row.avatar_url}`} alt="" data-testid={`avatar-${row.rank}`}
        className="rounded-full object-cover shrink-0" style={style} />
    );
  }
  const color = row?.samurai?.color || "#D4AF37";
  return (
    <div className="rounded-full flex items-center justify-center shrink-0 font-numeral font-black"
      data-testid={`avatar-${row?.rank}`}
      style={{ ...style, background: `${color}22`, border: ring ? "none" : `1px solid ${color}55`, color, fontSize: size * 0.42 }}>
      {row?.is_hitmax ? <Sparkle size={size * 0.5} weight="fill" color={color} /> : (row?.name?.[0] || "?").toUpperCase()}
    </div>
  );
}

// Barre d'acclamation du n°1 de la saison (réactions emoji).
function Cheer() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/play/reactions").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data || !data.leader || data.is_me) return null;
  const send = async (emoji) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/play/reactions", { emoji });
      setData((d) => ({ ...d, counts: res.counts, total: res.total, mine: res.mine }));
      toast.success(t("dojo.cheer_sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dojo.cheer_error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hm-card p-4 mt-3" data-testid="cheer-bar"
      style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.08), rgba(18,18,18,1))", borderColor: "rgba(212,175,55,0.3)" }}>
      <div className="flex items-center gap-3 mb-3">
        <DojoAvatar row={{ ...data.leader, rank: 1, samurai: { color: "#D4AF37" } }} size={40} ring="#D4AF37" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{t("dojo.cheer_title")}</p>
          <p className="text-sm font-bold truncate">{data.leader.name} · <span className="text-gold">{data.leader.points} pts</span></p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {data.emojis.map((em) => {
          const active = data.mine === em;
          const n = data.counts?.[em] || 0;
          return (
            <button key={em} onClick={() => send(em)} disabled={busy}
              data-testid={`cheer-${em}`}
              className={`flex items-center gap-1 px-2.5 h-9 rounded-full border text-sm font-bold transition-colors disabled:opacity-60 ${active ? "border-gold/70 bg-gold/20" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
              <span className="text-base leading-none">{em}</span>
              {n > 0 && <span className="text-[11px] text-white/60">{n}</span>}
            </button>
          );
        })}
        {data.total > 0 && <span className="text-[11px] text-white/40 ml-1">{t("dojo.cheer_total", { n: data.total })}</span>}
      </div>
    </div>
  );
}

const SPORT_TABS = [
  { k: "foot", label: "Football" },
  { k: "tennis", label: "Tennis" },
  { k: "basket", label: "Basket" },
];

const BIG_COMPS = ["ligue des champions", "champions league", "uefa champions",
  "europa league", "ligue europa", "uefa europa"];
const isBigMatch = (comp) => {
  const c = (comp || "").toLowerCase();
  return BIG_COMPS.some((k) => c.includes(k));
};

// Per-match countdown to its own kickoff (each match locks individually).
function MatchCountdown({ kickoff, locked }) {
  const { t } = useTranslation();
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const diff = new Date(kickoff) - new Date();
      if (diff <= 0) { setTxt(""); return; }
      const d = Math.floor(diff / 8.64e7), h = Math.floor((diff % 8.64e7) / 3.6e6), mn = Math.floor((diff % 3.6e6) / 6e4);
      if (d >= 1) setTxt(t("dojo.locks_days", { d, h }));
      else setTxt(h > 0 ? t("dojo.locks_h", { h, mn: String(mn).padStart(2, "0") }) : t("dojo.locks_min", { mn }));
    };
    tick(); const iv = setInterval(tick, 30000); return () => clearInterval(iv);
  }, [kickoff, t]);
  if (locked) return (
    <span className="text-[10px] font-bold text-white/45 inline-flex items-center gap-1" data-testid="match-closed-label">
      <Flag size={11} weight="fill" /> Clôturé
    </span>
  );
  if (!txt) return null;
  return (
    <span className="text-[10px] text-white/55 inline-flex items-center gap-1">
      <Lightning size={10} weight="fill" color="#00E676" /> {txt}
    </span>
  );
}

// ---------------------------------------------------------------- Rank badge
function RankBadge({ rank, size = "sm" }) {
  if (!rank) return null;
  const s = size === "lg" ? "text-sm px-3 py-1" : "text-[10px] px-2 py-0.5";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-black uppercase tracking-wide ${s}`}
      style={{ background: `${rank.color}1f`, border: `1px solid ${rank.color}66`, color: rank.color }}>
      <Sword size={size === "lg" ? 14 : 11} weight="fill" /> {rank.name}
    </span>
  );
}

// ---------------------------------------------------------------- Rank cards
const rankImg = (key) => `${process.env.PUBLIC_URL || ""}/ranks/${key}.jpg`;

function RankCardArt({ rankKey, name, locked, subtitle, glow, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden ${glow ? "rank-glow" : ""} ${className}`}
      data-testid={`rank-card-${rankKey}`}
      style={{ border: "2px solid rgba(0,230,118,0.85)", boxShadow: glow ? undefined : "0 0 18px rgba(0,230,118,0.30), inset 0 0 18px rgba(0,0,0,0.55)" }}>
      <img src={rankImg(rankKey)} alt={name} loading="lazy"
        className="w-full aspect-[5/4] object-cover object-top"
        style={{ filter: locked ? "grayscale(1) brightness(0.38)" : "none" }} />
      {locked && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45">
          <LockKey size={26} weight="fill" color="#00E676" />
          {subtitle && <span className="text-[11px] text-white/80 mt-1 font-black">{subtitle}</span>}
        </div>
      )}
    </div>
  );
}

function RanksModal({ me, onClose }) {
  const { t } = useTranslation();
  const curIdx = me.ranks.findIndex((r) => r.key === me.samurai.key);
  return (
    <div className="fixed inset-0 z-[70] bg-black/88 backdrop-blur-sm overflow-y-auto" data-testid="ranks-modal" onClick={onClose}>
      <div className="max-w-lg mx-auto px-5 py-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-black">{t("dojo.way_title")}</h3>
          <button data-testid="ranks-close" onClick={onClose} className="text-white/60 hover:text-white"><XCircle size={28} weight="fill" /></button>
        </div>
        <p className="text-[11px] text-white/45 mb-5">{t("dojo.way_sub")}</p>
        <div className="space-y-5">
          {me.ranks.map((r, i) => {
            const unlocked = i <= curIdx;
            return (
              <div key={r.key} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <RankCardArt rankKey={r.key} name={r.name} locked={!unlocked} subtitle={unlocked ? null : `${r.min} pts`} />
                <div className="flex items-center justify-between mt-2 px-1">
                  <span className="text-base font-black" style={{ color: unlocked ? r.color : "#5a5a5a" }}>{r.name}</span>
                  <span className="text-[11px] font-bold" style={{ color: i === curIdx ? "#00E676" : "rgba(255,255,255,0.4)" }}>
                    {i === curIdx ? t("dojo.current_rank") : unlocked ? t("dojo.unlocked") : `${r.min} pts`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function UnlockModal({ rank, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[80] bg-black/93 flex items-center justify-center px-6" data-testid="rank-unlock-modal" onClick={onClose}>
      <div className="max-w-sm w-full text-center rank-unlock-pop" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs uppercase tracking-[0.3em] font-black mb-3" style={{ color: "#00E676" }}>{t("dojo.rank_unlocked")}</p>
        <RankCardArt rankKey={rank.key} name={rank.name} glow />
        <p className="font-display text-3xl font-black mt-4" style={{ color: rank.color }}>{rank.name}</p>
        <p className="text-sm text-white/55 mt-1">{t("dojo.progress_samurai")}</p>
        <Button data-testid="rank-unlock-close" onClick={onClose} className="mt-5 h-11 px-8 bg-gold text-black hover:bg-gold-dark font-bold glow-gold">{t("dojo.continue")}</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Play header
function PlayProfile({ me, onOpenRanks, onShare }) {
  const { t } = useTranslation();
  if (!me) return <div className="h-24 rounded-2xl skeleton mt-4" />;
  const r = me.samurai;
  const pct = r.next_at ? Math.min(100, Math.round((r.points - (me.ranks.find(x => x.key === r.key)?.min || 0)) / (r.next_at - (me.ranks.find(x => x.key === r.key)?.min || 0)) * 100)) : 100;
  return (
    <div className="hm-card p-4 mt-4 fade-up" data-testid="play-profile"
      style={{ background: `linear-gradient(135deg, ${r.color}12, rgba(18,18,18,1))`, borderColor: `${r.color}44` }}>
      <div className="flex items-center justify-between">
        <button onClick={onOpenRanks} data-testid="open-ranks" className="flex items-center gap-3 text-left">
          <RankCardArt rankKey={r.key} name={r.name} className="w-16 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("dojo.your_rank")}</p>
            <span className="font-display text-2xl font-black block leading-tight" style={{ color: r.color }}>{r.name}</span>
            <span className="text-[10px] text-perf-excellent font-bold">{t("dojo.see_way")}</span>
          </div>
        </button>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1.5 justify-end" data-testid="play-streak">
            <Fire size={20} weight="fill" color={me.pred_streak > 0 ? "#FB8C00" : "#555"} />
            <span className="font-numeral font-black text-2xl">{me.pred_streak}</span>
          </div>
          <p className="text-[10px] text-white/40">{t("dojo.streak_record", { n: me.best_streak })}</p>
        </div>
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-[10px] text-white/45 mb-1">
          <span>{r.points} pts</span>
          {r.next ? <span>{t("dojo.next_at", { rank: r.next, pts: r.next_at })}</span> : <span>{t("dojo.rank_max")}</span>}
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: r.color }} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-white/5 rounded-lg py-2">
          <p className="font-numeral font-black text-gold">{me.season_points}</p>
          <p className="text-[9px] text-white/40">{t("dojo.pts_month")}</p>
        </div>
        <div className="bg-white/5 rounded-lg py-2">
          <p className="font-numeral font-black">{me.picks_correct}/{me.picks_total}</p>
          <p className="text-[9px] text-white/40">{t("dojo.good_preds")}</p>
        </div>
        <div className="bg-white/5 rounded-lg py-2">
          <p className="font-numeral font-black">{me.accuracy}%</p>
          <p className="text-[9px] text-white/40">{t("dojo.accuracy")}</p>
        </div>
      </div>
      <button onClick={onShare} data-testid="share-rank-podium-btn"
        className="w-full mt-3 h-10 rounded-lg flex items-center justify-center gap-2 text-xs font-bold transition-colors"
        style={{ background: `${r.color}1f`, border: `1px solid ${r.color}55`, color: r.color }}>
        <ShareNetwork size={16} weight="bold" /> {t("dojo.share_rank")}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- Grille card
function MatchPick({ m, pick, locked, onWinner, onScore, onSets }) {
  const { t } = useTranslation();
  const sides = m.has_draw ? ["home", "draw", "away"] : ["home", "away"];
  const label = { home: m.home.short, draw: t("dojo.draw"), away: m.away.short };
  const res = m.result;
  const d = m.detail;
  const setOpts = m.sport === "tennis"
    ? (m.best_of === 5 ? ["3-0", "3-1", "3-2", "2-3", "1-3", "0-3"] : ["2-0", "2-1", "1-2", "0-2"])
    : [];
  const big = isBigMatch(m.competition);
  return (
    <div className={`hm-card p-4 relative ${big ? "grid-big-match" : ""}`} data-testid={`grid-match-${m.mid}`}
      style={big ? { border: "1px solid rgba(212,175,55,0.55)", boxShadow: "0 0 16px rgba(212,175,55,0.15)" } : undefined}>
      {big && (
        <span className="absolute -top-2 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-gold text-black"
          data-testid={`grid-big-badge-${m.mid}`}>
          <Sparkle size={10} weight="fill" /> Grand match
        </span>
      )}
      <div className="flex items-center justify-between mb-1 mt-1">
        <span className={`text-[10px] font-bold uppercase tracking-widest truncate max-w-[55%] ${big ? "text-gold" : "text-gold/80"}`}>{m.competition}</span>
        <span className="text-[10px] text-white/40">{kickoffDate(m, { day: "2-digit", month: "short" })} · {kickoffTimeTz(m)}</span>
      </div>
      <div className="flex justify-end mb-3">
        <MatchCountdown kickoff={m.kickoff} locked={locked} />
      </div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <TeamBadge team={m.home} size={34} />
          <span className="text-[11px] font-semibold text-center leading-tight truncate w-full">{m.home.name}</span>
        </div>
        <span className="font-numeral font-black text-white/25 text-xs">VS</span>
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <TeamBadge team={m.away} size={34} />
          <span className="text-[11px] font-semibold text-center leading-tight truncate w-full">{m.away.name}</span>
        </div>
      </div>

      <div className={`grid gap-1.5 ${m.has_draw ? "grid-cols-3" : "grid-cols-2"}`}>
        {sides.map((s) => {
          const chosen = pick?.winner === s;
          const isResult = res?.finished && res.winner === s;
          return (
            <button key={s} disabled={locked} data-testid={`pick-${m.mid}-${s}`}
              onClick={() => onWinner(m.mid, s)}
              className={`py-2 rounded-lg text-xs font-bold border transition-colors ${
                isResult ? "bg-perf-excellent/20 border-perf-excellent text-white"
                : chosen ? "bg-gold/20 border-gold text-white" : "border-white/10 text-white/60"}`}>
              {label[s]}
            </button>
          );
        })}
      </div>

      {/* exact score bonus */}
      {!locked && pick?.winner && m.has_score && m.sport === "foot" && (
        <div className="flex items-center justify-center gap-2 mt-3" data-testid={`score-${m.mid}`}>
          <span className="text-[10px] text-white/40">Score exact (bonus +15)</span>
          <input type="number" min="0" data-testid={`score-${m.mid}-home`} value={pick?.score?.home ?? ""}
            onChange={(e) => onScore(m.mid, "home", e.target.value)}
            className="w-10 h-9 rounded-lg bg-white/5 border border-white/10 text-center font-numeral font-bold text-white" />
          <span className="text-white/30">-</span>
          <input type="number" min="0" data-testid={`score-${m.mid}-away`} value={pick?.score?.away ?? ""}
            onChange={(e) => onScore(m.mid, "away", e.target.value)}
            className="w-10 h-9 rounded-lg bg-white/5 border border-white/10 text-center font-numeral font-bold text-white" />
        </div>
      )}
      {!locked && pick?.winner && m.has_score && m.sport === "tennis" && (
        <div className="mt-3" data-testid={`sets-${m.mid}`}>
          <p className="text-[10px] text-white/40 text-center mb-1.5">Score en sets (bonus +15)</p>
          <div className="flex gap-1.5 justify-center flex-wrap">
            {setOpts.map((so) => (
              <button key={so} data-testid={`sets-${m.mid}-${so}`} onClick={() => onSets(m.mid, so)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold border ${pick?.sets === so ? "bg-gold/20 border-gold text-white" : "border-white/10 text-white/50"}`}>{so}</button>
            ))}
          </div>
        </div>
      )}

      {/* HITMAX pick + result (after lock) */}
      {locked && (
        <div className="mt-3 flex items-center justify-between gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 text-white/60">
            <Sparkle size={12} weight="fill" color="#D4AF37" /> HITMAX : <b className="text-gold">{label[m.hitmax] || "—"}</b>
          </span>
          {res?.finished ? (
            <span className="font-numeral font-bold text-white/70">{res.score}</span>
          ) : (
            <span className="text-white/40">en attente…</span>
          )}
        </div>
      )}
      {locked && d && (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`result-${m.mid}`}>
          {d.winner_ok ? <CheckCircle size={16} weight="fill" color="#00E676" /> : <XCircle size={16} weight="fill" color="#E53935" />}
          {d.exact_ok && <span className="text-[10px] font-black text-gold">SCORE EXACT</span>}
          {d.beat_hitmax && <span className="text-[10px] font-black text-perf-excellent inline-flex items-center gap-0.5"><Sword size={11} weight="fill" /> BAT HITMAX</span>}
          <span className="ml-auto font-numeral font-black text-gold">+{d.pts}</span>
        </div>
      )}
    </div>
  );
}

function Grilles() {
  const { t } = useTranslation();
  const [sport, setSport] = useState("foot");
  const [grid, setGrid] = useState(null);
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback((sp) => {
    setGrid(null);
    api.get(`/play/grids?sport=${sp}`).then((r) => {
      setGrid(r.data);
      const init = {};
      (r.data.matches || []).forEach((m) => { if (m.my_pick) init[m.mid] = m.my_pick; });
      setPicks(init);
    }).catch(() => setGrid({ matches: [], status: "empty" }));
  }, []);
  useEffect(() => { load(sport); }, [sport, load]);

  const onWinner = (mid, s) => setPicks((p) => ({ ...p, [mid]: { ...(p[mid] || {}), winner: s } }));
  const onScore = (mid, side, v) => setPicks((p) => ({ ...p, [mid]: { ...(p[mid] || {}), score: { ...(p[mid]?.score || {}), [side]: v === "" ? null : parseInt(v) } } }));
  const onSets = (mid, so) => setPicks((p) => ({ ...p, [mid]: { ...(p[mid] || {}), sets: p[mid]?.sets === so ? null : so } }));

  const submit = async () => {
    const clean = {};
    Object.entries(picks).forEach(([mid, p]) => { if (p?.winner) clean[mid] = p; });
    if (!Object.keys(clean).length) { toast.error("Fais au moins un pronostic"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/play/grids/submit", { sport, picks: clean });
      toast.success(`Grille validée · ${data.count} pronos`);
      load(sport);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'enregistrer"); }
    setSaving(false);
  };

  return (
    <div className="mt-4" data-testid="grilles-panel">
      <div className="flex gap-1.5 mb-3">
        {SPORT_TABS.map((s) => (
          <button key={s.k} data-testid={`grille-sport-${s.k}`} onClick={() => setSport(s.k)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${sport === s.k ? "bg-gold text-black border-gold" : "border-white/10 text-white/55"}`}>{s.label}</button>
        ))}
      </div>

      {!grid ? <div className="h-64 rounded-2xl skeleton" />
        : !grid.matches?.length ? (
          <div className="hm-card p-8 text-center" data-testid="grille-empty">
            <Trophy size={30} weight="fill" color="#555" className="mx-auto" />
            <p className="text-sm text-white/50 mt-2">{t("dojo.grid_empty")}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-1.5">
                {grid.locked ? <Flag size={15} weight="fill" color="#00E676" /> : <Lightning size={15} weight="fill" color="#00E676" />}
                <span className="text-xs font-bold">{grid.locked ? t("dojo.grid_closed") : t("dojo.grid_make")}</span>
              </div>
              {!grid.locked && (() => {
                const open = grid.matches.filter((m) => !m.locked).length;
                return <span className="text-[11px] text-white/50" data-testid="grille-open-count">{t("dojo.open_count", { n: open, p: open > 1 ? "s" : "", pe: open > 1 ? "es" : "" })}</span>;
              })()}
            </div>

            {grid.submitted && grid.points != null && (
              <div className="hm-card hm-card-active p-3 mb-3 text-center" data-testid="grille-points">
                <p className="font-numeral font-black text-2xl gold-text">+{grid.points} pts</p>
                <p className="text-[11px] text-white/50">ta grille de {SPORT_TABS.find(s => s.k === sport)?.label}</p>
              </div>
            )}

            <div className="space-y-3">
              {grid.matches.map((m) => (
                <MatchPick key={m.mid} m={m} pick={picks[m.mid]} locked={m.locked}
                  onWinner={onWinner} onScore={onScore} onSets={onSets} />
              ))}
            </div>

            {grid.matches.some((m) => !m.locked) && (
              <Button data-testid="grille-submit" onClick={submit} disabled={saving}
                className="w-full h-12 mt-4 bg-gold text-black hover:bg-gold-dark font-bold glow-gold">
                {grid.submitted ? "Modifier ma grille" : "Valider ma grille"}
              </Button>
            )}
            <p className="text-[11px] text-white/40 text-center mt-3">
              Bon vainqueur +10 · bat HITMAX +5 · score exact +15 · bonus de série 🔥
            </p>
          </>
        )}
    </div>
  );
}

// ---------------------------------------------------------------- Standings
const PODIUM = ["#D4AF37", "#C0C0C0", "#CD7F32"];

function Podium({ podium }) {
  if (!podium?.length) return null;
  const order = [1, 0, 2].filter((i) => podium[i]);
  const heights = { 0: "h-24", 1: "h-16", 2: "h-12" };
  return (
    <div className="flex items-end justify-center gap-3 mt-4 mb-2" data-testid="podium">
      {order.map((i) => {
        const p = podium[i];
        return (
          <div key={i} className="flex flex-col items-center flex-1 max-w-[110px]">
            <div className="flex flex-col items-center mb-1">
              {p.is_hitmax ? <Sparkle size={18} weight="fill" color={PODIUM[i]} /> : <Medal size={18} weight="fill" color={PODIUM[i]} />}
              <div className="my-1">
                <DojoAvatar row={p} size={i === 0 ? 48 : 40} ring={PODIUM[i]} />
              </div>
              <span className="text-[11px] font-bold truncate max-w-full mt-0.5 flex items-center gap-0.5">
                {p.titles > 0 && <Crown size={11} weight="fill" color="#D4AF37" />}{p.name}
              </span>
              <span className="font-numeral font-black text-sm" style={{ color: PODIUM[i] }}>{p.points}</span>
            </div>
            <div className={`w-full ${heights[i]} rounded-t-lg flex items-start justify-center pt-1`}
              style={{ background: `${PODIUM[i]}22`, border: `1px solid ${PODIUM[i]}55` }}>
              <span className="font-numeral font-black text-lg" style={{ color: PODIUM[i] }}>{p.rank}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Modale récap de fin de saison (affichée une fois à l'ouverture du Dojo).
function SeasonRecap() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    api.get("/play/season-recap").then((r) => {
      if (r.data?.available && !r.data.seen) { setData(r.data); setOpen(true); }
    }).catch(() => {});
  }, []);
  const close = async () => {
    setOpen(false);
    try { await api.post("/play/season-recap/seen"); } catch {}
  };
  if (!open || !data) return null;
  const c = data.champion || {};
  const shareStatic = {
    avatarName: user?.name,
    avatarColor: "#D4AF37",
    badge: c.is_me ? t("dojo.recap_champion_you") : data.label,
    bigValue: data.my_rank ? `#${data.my_rank}` : String(data.my_points),
    band: data.my_rank && data.my_rank <= 3 ? "excellent" : "good",
  };
  const shareLoc = (tt) => ({
    title: tt("share.recap_title", { label: data.label }),
    unit: data.my_rank ? "" : " pts",
    subtitle: `${data.my_points} ${tt("share.points_month")}`,
    context: `${tt("dojo.recap_champion")}: ${c.is_me ? tt("dojo.recap_champion_you") : (c.name || "—")}`,
    shareText: tt("share.recap_text", { label: data.label, rank: data.my_rank ? `#${data.my_rank}` : `${data.my_points} pts` }),
  });
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" data-testid="season-recap-modal" onClick={close}>
      <div className="w-full max-w-sm bg-[#141414] border border-gold/40 rounded-2xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
        <p className="text-[11px] uppercase tracking-[0.3em] font-black text-gold">{t("dojo.recap_title")}</p>
        <p className="font-display text-2xl font-black mt-1 capitalize">{data.label}</p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/40">{t("dojo.recap_your_rank")}</p>
            <p className="font-numeral font-black text-2xl mt-1" data-testid="recap-rank">{data.my_rank ? `#${data.my_rank}` : "—"}</p>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/40">{t("dojo.recap_your_points")}</p>
            <p className="font-numeral font-black text-2xl mt-1 text-gold">{data.my_points}</p>
          </div>
        </div>
        {!data.participated && <p className="text-[12px] text-white/45 mt-3">{t("dojo.recap_not_played")}</p>}

        <div className="mt-5 rounded-xl p-3 flex items-center gap-3" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(18,18,18,1))", border: "1px solid rgba(212,175,55,0.4)" }}>
          <DojoAvatar row={{ ...c, rank: 1, samurai: { color: "#D4AF37" } }} size={40} ring="#D4AF37" />
          <div className="text-left min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1"><Crown size={11} weight="fill" color="#D4AF37" /> {t("dojo.recap_champion")}</p>
            <p className="text-sm font-bold truncate">{c.is_me ? t("dojo.recap_champion_you") : c.name}</p>
          </div>
          <span className="ml-auto font-numeral font-black text-gold shrink-0">{c.points}<span className="text-[10px] text-white/40 ml-0.5">pts</span></span>
        </div>

        <button onClick={() => { trackEvent("share_recap_click"); setShareOpen(true); }} data-testid="recap-share-btn"
          className="w-full h-11 rounded-xl bg-white/8 border border-gold/40 text-gold font-bold text-sm hover:bg-white/12 transition-colors mt-6 flex items-center justify-center gap-2">
          <ShareNetwork size={16} weight="bold" /> {t("dojo.recap_share")}
        </button>
        <button onClick={close} data-testid="recap-close"
          className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors mt-2">
          {t("dojo.recap_cta")}
        </button>
      </div>

      <ShareCard open={shareOpen} onOpenChange={setShareOpen} {...shareStatic} localize={shareLoc}
        onShared={() => trackEvent("share_recap_confirmed")} />
    </div>
  );
}

function StandingRow({ r }) {
  return (
    <div data-testid={`standing-${r.rank}`}
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-white/5 last:border-0 ${r.is_me ? "bg-gold/10" : r.is_hitmax ? "bg-white/[0.03]" : ""}`}>
      <span className="w-6 text-center font-numeral font-black text-white/50">{r.rank}</span>
      <DojoAvatar row={r} size={34} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {r.is_hitmax && <Sparkle size={13} weight="fill" color="#D4AF37" />}
          <p className="text-sm font-bold truncate">{r.name}{r.is_me ? " (toi)" : ""}</p>
          {r.titles > 0 && (
            <span className="inline-flex items-center gap-0.5 text-gold shrink-0" data-testid={`crown-${r.rank}`}
              title={`${r.titles} titre(s) de champion`}>
              <Crown size={12} weight="fill" />
              {r.titles > 1 && <span className="text-[10px] font-black">{r.titles}</span>}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <RankBadge rank={r.samurai} />
          <span className="text-[10px] text-white/40">{r.correct}/{r.total} · {r.accuracy}%</span>
        </div>
      </div>
      <span className="font-numeral font-black text-gold">{r.points}<span className="text-[10px] text-white/40 ml-0.5">pts</span></span>
      {!r.is_me && !r.is_hitmax && r.user_id && (
        <>
          <button type="button" onClick={() => reportPlayer(r)} aria-label={`Signaler ${r.name}`}
            className="p-1.5 text-white/30 hover:text-white/70" data-testid={`report-${r.rank}`}>
            <Flag size={14} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => blockPlayer(r)} aria-label={`Bloquer ${r.name}`}
            className="p-1.5 text-white/30 hover:text-white/70" data-testid={`block-${r.rank}`}>
            <Prohibit size={14} aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}

// Audit F4 : bloquer un joueur (il disparaît de mes classements ; déblocage depuis « Mon compte »).
async function blockPlayer(r) {
  if (!window.confirm(`Bloquer ${r.name} ? Il n'apparaîtra plus dans tes classements et ses acclamations ne te seront plus notifiées.`)) return;
  try {
    await api.post("/blocks", { user_id: r.user_id });
    toast.success(`${r.name} est bloqué.`);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Blocage impossible pour le moment.");
  }
}

// Audit conformité F4 : signalement d'un pseudo ou d'un avatar inapproprié.
async function reportPlayer(r) {
  const comment = window.prompt(`Signaler ${r.name} (pseudo ou avatar inapproprié). Précise le problème (facultatif) :`);
  if (comment === null) return;
  try {
    await api.post("/reports", { target_type: "user", target_id: r.user_id, reason: "offensant", comment: comment.slice(0, 500) });
    toast.success("Merci, le signalement a été transmis à l'équipe HitMax.");
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Signalement impossible pour le moment.");
  }
}

function Classement({ onShare }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/play/standings?scope=global").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <div className="h-64 rounded-2xl skeleton mt-4" />;

  return (
    <div className="mt-4" data-testid="classement-panel">
      <div className="hm-card p-4 mb-3" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.1), rgba(18,18,18,1))", borderColor: "rgba(212,175,55,0.35)" }}>
        <div className="flex items-center gap-2">
          <Trophy size={20} weight="fill" color="#D4AF37" />
          <div>
            <p className="text-sm font-black gold-text">{t("dojo.season", { label: data.season.label })}</p>
            <p className="text-[11px] text-white/55">{t(DOJO_PRIZES_ENABLED ? "dojo.season_sub_pre" : "dojo.season_sub_pre_noprize")}<b className="text-white/80">{t("dojo.days_left", { days: data.season.days_left })}</b></p>
          </div>
        </div>
      </div>

      <Button onClick={() => onShare("classement")} data-testid="share-ranking-btn"
        className="w-full mb-3 font-bold text-black" style={{ background: "#D4AF37" }}>
        <ShareNetwork size={18} weight="bold" /> <span className="ml-2">{t("dojo.share_ranking")}</span>
      </Button>

      <Podium podium={data.podium} />
      <Cheer />
      <div className="hm-card overflow-hidden mt-2">
        {data.rows.length ? data.rows.map((r) => <StandingRow key={r.rank} r={r} />)
          : <p className="text-sm text-white/40 p-6 text-center">{t("dojo.empty_month")}</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Leagues
function LeagueStandings({ league, onBack }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/play/standings?scope=league&league_id=${league.league_id}`).then((r) => setData(r.data)).catch(() => {}); }, [league]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(league.code);
      toast.success(t("dojo.toast_copied"));
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = league.code; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(t("dojo.toast_copied"));
      } catch {
        toast.error(t("dojo.toast_copy_fail", { code: league.code }));
      }
    }
  };
  return (
    <div className="mt-4" data-testid="league-standings">
      <button data-testid="league-back" onClick={onBack} className="flex items-center gap-1 text-xs text-white/60 mb-3"><CaretLeft size={14} weight="bold" /> {t("dojo.my_leagues")}</button>
      <div className="hm-card p-4 mb-3 flex items-center justify-between">
        <div>
          <p className="font-display text-lg font-black">{league.name}</p>
          <p className="text-[11px] text-white/50">{t("dojo.members_hitmax", { n: league.members })}</p>
        </div>
        <button data-testid="league-copy-code" onClick={copy} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs font-bold">
          <Copy size={14} weight="bold" /> {league.code}
        </button>
      </div>
      {!data ? <div className="h-40 rounded-2xl skeleton" /> : (
        <>
          <Podium podium={data.podium} />
          <div className="hm-card overflow-hidden mt-2">
            {data.rows.map((r) => <StandingRow key={r.rank} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}

// Audit F4 : signalement d'un nom de ligue inapproprié.
async function reportLeague(lg, t) {
  const comment = window.prompt(t("community.report_league_prompt", { name: lg.name }));
  if (comment === null) return;
  try {
    await api.post("/reports", { target_type: "league", target_id: lg.league_id, reason: "offensant", comment: comment.slice(0, 500) });
    toast.success(t("community.report_sent"));
  } catch (err) {
    toast.error(err?.response?.data?.detail || t("dojo.toast_error"));
  }
}

function Leagues() {
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [active, setActive] = useState(null);
  const [rulesOk, setRulesOk] = useState(false);

  const load = useCallback(() => api.get("/play/leagues").then((r) => setLeagues(r.data.leagues)).catch(() => setLeagues([])), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error(t("dojo.toast_name_required")); return; }
    try { const { data } = await api.post("/play/leagues", { name, accept_community_rules: rulesOk }); toast.success(t("dojo.toast_league_created", { code: data.code })); setName(""); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t("dojo.toast_error")); }
  };
  const join = async () => {
    if (!code.trim()) { toast.error(t("dojo.toast_code_required")); return; }
    try { const { data } = await api.post("/play/leagues/join", { code }); toast.success(t("dojo.toast_joined", { name: data.name })); setCode(""); load(); }
    catch { toast.error(t("dojo.toast_code_invalid")); }
  };

  if (active) return <LeagueStandings league={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="mt-4 space-y-3" data-testid="leagues-panel">
      <div className="hm-card p-4">
        <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><Plus size={14} weight="bold" /> {t("dojo.create_league")}</p>
        <div className="flex gap-2">
          <input data-testid="league-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("dojo.league_name_ph")}
            className="flex-1 h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          <Button data-testid="league-create-btn" onClick={create} className="h-11 bg-gold text-black hover:bg-gold-dark font-bold px-4">{t("dojo.create")}</Button>
        </div>
        <label className="flex items-start gap-2 mt-2 text-[11px] text-white/60 cursor-pointer">
          <input type="checkbox" checked={rulesOk} onChange={(e) => setRulesOk(e.target.checked)} className="mt-0.5" data-testid="league-rules-check" />
          <span>{t("community.accept_prefix")} <a href="/regles-communaute" className="underline">{t("community.rules_link")}</a>.</span>
        </label>
      </div>
      <div className="hm-card p-4">
        <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><Users size={14} weight="fill" /> {t("dojo.join_code")}</p>
        <div className="flex gap-2">
          <input data-testid="league-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("dojo.code_ph")}
            className="flex-1 h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white uppercase tracking-widest font-bold" />
          <Button data-testid="league-join-btn" onClick={join} className="h-11 bg-white/10 text-white hover:bg-white/20 font-bold px-4">{t("dojo.join")}</Button>
        </div>
      </div>

      {leagues === null ? <div className="h-24 rounded-2xl skeleton" />
        : !leagues.length ? <p className="text-sm text-white/40 text-center py-6">{t("dojo.empty_leagues")}</p>
          : leagues.map((lg) => (
            <button key={lg.league_id} data-testid={`league-item-${lg.league_id}`} onClick={() => setActive(lg)}
              className="hm-card p-4 w-full flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gold/15 border border-gold/40 flex items-center justify-center"><Sword size={18} weight="fill" color="#D4AF37" /></div>
                <div>
                  <p className="text-sm font-bold">{lg.name}</p>
                  <p className="text-[11px] text-white/45">{t("dojo.members_code", { n: lg.members, code: lg.code })}</p>
                </div>
              </div>
              <span className="flex items-center gap-2">
                <span role="button" tabIndex={0} aria-label={t("community.report_league")} data-testid={`league-report-${lg.league_id}`}
                  onClick={(e) => { e.stopPropagation(); reportLeague(lg, t); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); reportLeague(lg, t); } }}
                  className="p-1.5 text-white/30 hover:text-white/70 text-[11px] underline">{t("community.report")}</span>
                <Flag size={16} weight="fill" color="#D4AF37" />
              </span>
            </button>
          ))}
    </div>
  );
}

// ---------------------------------------------------------------- Quiz (kept)
function Quiz() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [quizFailed, setQuizFailed] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/quiz/daily").then((r) => setQuiz(r.data)).catch(() => setQuizFailed(true)); }, []);
  if (quizFailed) {
    return (
      <div className="h-40 rounded-2xl mt-4 flex flex-col items-center justify-center gap-2 text-center text-white/50 text-sm border border-white/10">
        <span>{t("dojo.quiz_fail")}</span>
        <button className="text-gold underline text-xs" onClick={() => { setQuizFailed(false); api.get("/quiz/daily").then((r) => setQuiz(r.data)).catch(() => setQuizFailed(true)); }}>{t("dojo.retry")}</button>
      </div>
    );
  }
  if (!quiz) return <div className="h-40 rounded-2xl skeleton mt-4" />;
  const submit = async () => {
    const arr = quiz.questions.map((_, i) => answers[i] ?? -1);
    const { data } = await api.post("/quiz/submit", { answers: arr });
    setResult(data); await refreshUser(); toast.success(`+${data.xp_gained} XP`);
  };
  const allAnswered = Object.keys(answers).length === quiz.questions.length;
  return (
    <div className="mt-4 space-y-3" data-testid="quiz-panel">
      {result && (
        <div className="hm-card hm-card-active p-4 text-center">
          <Trophy size={32} weight="fill" color="#D4AF37" className="mx-auto" />
          <p className="font-numeral font-black text-4xl gold-text mt-2">{result.score}/{result.total}</p>
          <p className="text-sm text-white/60">+{result.xp_gained} XP · {t("play.level", { name: result.level.name })}</p>
        </div>
      )}
      {quiz.questions.map((q, qi) => (
        <div key={qi} className="hm-card p-4">
          <p className="text-sm font-semibold mb-3">{qi + 1}. {q.q}</p>
          <div className="grid grid-cols-2 gap-2">
            {q.options.map((opt, oi) => {
              const chosen = answers[qi] === oi;
              const correct = result && result.corrections[qi] === oi;
              const wrong = result && chosen && result.corrections[qi] !== oi;
              return (
                <button key={oi} data-testid={`quiz-${qi}-${oi}`} disabled={!!result}
                  onClick={() => setAnswers({ ...answers, [qi]: oi })}
                  className={`p-2.5 rounded-lg text-sm font-medium border text-left flex items-center justify-between ${
                    correct ? "bg-perf-good/20 border-perf-good text-white"
                    : wrong ? "bg-perf-danger/20 border-perf-danger text-white"
                    : chosen ? "bg-gold/15 border-gold text-white" : "border-white/10 text-white/70"}`}>
                  {opt}
                  {correct && <CheckCircle size={16} weight="fill" color="#43A047" />}
                  {wrong && <XCircle size={16} weight="fill" color="#E53935" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!result && (
        <Button data-testid="quiz-submit" onClick={submit} disabled={!allAnswered || quiz.completed}
          className="w-full h-12 bg-gold text-black hover:bg-gold-dark font-bold glow-gold">
          {quiz.completed ? t("play.quiz_done") : t("play.quiz_submit")}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Gate
function SubGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="px-5 pt-16 flex flex-col items-center text-center" data-testid="play-sub-gate">
      <div className="w-16 h-16 rounded-2xl bg-gold/15 border border-gold/50 flex items-center justify-center">
        <Sword size={30} weight="fill" color="#D4AF37" />
      </div>
      <h2 className="font-display text-2xl font-black mt-4">{t("dojo.gate_title")}</h2>
      <p className="text-sm text-white/55 mt-2 max-w-xs leading-relaxed">
        {t(DOJO_PRIZES_ENABLED ? "dojo.gate_desc" : "dojo.gate_desc_noprize")}
      </p>
      <Button data-testid="play-gate-cta" onClick={() => navigate("/passer-pro")}
        className="mt-6 h-12 px-6 bg-gold text-black hover:bg-gold-dark font-bold glow-gold">
        <Crown size={16} weight="fill" className="mr-1.5" /> {t("dojo.gate_cta")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------- Page
export default function Play() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("grilles");
  const [me, setMe] = useState(null);
  const [showRanks, setShowRanks] = useState(false);
  const [unlockRank, setUnlockRank] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [seasonLabel, setSeasonLabel] = useState("");
  const subbed = hasSub(user);

  useEffect(() => {
    if (subbed) {
      api.get("/play/me").then((r) => {
        setMe(r.data);
        if (r.data.just_unlocked) setUnlockRank(r.data.just_unlocked);
        setSeasonLabel(r.data?.season?.label || "");
      }).catch(() => {});
      api.get("/play/standings?scope=global").then((r) => {
        const mine = (r.data.rows || []).find((x) => x.is_me);
        setMyRank(mine ? mine.rank : null);
        if (r.data?.season?.label) setSeasonLabel(r.data.season.label);
      }).catch(() => {});
    }
  }, [subbed]);

  if (!subbed) return (<><Header showSearch={false} /><SubGate /></>);

  const samurai = me?.samurai;
  const shareStatic = {
    avatarName: user?.name || me?.name,
    avatarColor: samurai?.color,
    badge: samurai?.name,
    bigValue: myRank ? `#${myRank}` : String(me?.play_points ?? 0),
    band: myRank && myRank <= 3 ? "excellent" : "good",
  };
  const shareLoc = (tt) => ({
    title: tt("share.dojo_title"),
    unit: myRank ? "" : ` ${tt("dojo.pts_month").split(" ")[0]}`,
    subtitle: myRank
      ? `${me?.season_points ?? 0} ${tt("share.points_month")} · ${samurai?.name || ""}`
      : `${samurai?.name || "Novice"} · ${me?.play_points ?? 0} pts`,
    context: `${tt("dojo.season", { label: seasonLabel })} · ${tt("dojo.accuracy")} ${me?.accuracy ?? 0}%`,
    shareText: myRank
      ? tt("share.ranking_share_text", { rank_ord: `#${myRank}` })
      : tt("share.rank_share_text", { rank: samurai?.name || "Novice" }),
  });

  const openShare = (source) => {
    trackEvent("share_ranking_click");
    setShareOpen(true);
  };

  const TABS = [
    ["grilles", t("dojo.tab_grilles"), Target],
    ["ligues", t("dojo.tab_ligues"), Users],
    ["classement", t("dojo.tab_classement"), Trophy],
    ["quiz", t("dojo.tab_quiz"), Sparkle],
  ];
  return (
    <>
      <Header showSearch={false} />
      <SeasonRecap />
      <div className="px-5 pt-5">
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("dojo.dojo_title")}</h1>
        <p className="text-sm text-white/50 mt-1">{t("dojo.dojo_sub")}</p>

        <PlayProfile me={me} onOpenRanks={() => setShowRanks(true)} onShare={() => openShare("podium")} />

        <div className="flex gap-1 p-1 mt-4 rounded-lg bg-white/5">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`play-tab-${k}`} onClick={() => setTab(k)}
              className={`flex-1 py-2.5 rounded-md text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${tab === k ? "bg-gold text-black" : "text-white/60"}`}>
              <Icon size={15} weight="fill" /> <span className="truncate">{l}</span>
            </button>
          ))}
        </div>

        {tab === "grilles" && <Grilles />}
        {tab === "ligues" && <Leagues />}
        {tab === "classement" && <Classement onShare={openShare} />}
        {tab === "quiz" && <Quiz />}
        <div className="h-8" />
      </div>

      <ShareCard open={shareOpen} onOpenChange={setShareOpen} {...shareStatic} localize={shareLoc}
        onShared={() => trackEvent("share_ranking_confirmed")} />
      {showRanks && me && <RanksModal me={me} onClose={() => setShowRanks(false)} />}
      {unlockRank && <UnlockModal rank={unlockRank} onClose={() => setUnlockRank(null)} />}
    </>
  );
}
